import React, { useState, useEffect } from 'react';
import { UserPlus, Edit, Trash2, Save, X, RotateCcw, Sparkles, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { NativeSelect } from './ui/native-select';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { usersAPI } from '../services/api';
import api from '../services/api';
import { toast } from 'sonner';

const ColaboradoresTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showInativos, setShowInativos] = useState(false);
  const [desativarTarget, setDesativarTarget] = useState(null); // { id, nome }
  const [desativarMotivo, setDesativarMotivo] = useState('saida_empresa');
  const [desativarObs, setDesativarObs] = useState('');
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    nome: '',
    password: '',
    role: 'colaborador',
    ativo: true,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
      const mensagem = error.response?.data?.detail || error.message || 'Erro desconhecido';
      toast.error(`Erro ao carregar colaboradores: ${mensagem}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({
      username: '',
      nome: '',
      password: '',
      role: 'colaborador',
      ativo: true,
      mostrar_em_horario: true,
    });
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setIsAdding(false);
    setFormData({
      username: user.username,
      nome: user.nome,
      password: '',
      role: user.role,
      ativo: user.ativo,
      mostrar_em_horario: user.mostrar_em_horario !== undefined ? user.mostrar_em_horario : true,
    });
  };

  const handleSave = async () => {
    try {
      if (isAdding) {
        await usersAPI.create(formData);
        toast.success('Colaborador criado com sucesso!');
      } else {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await usersAPI.update(editingId, updateData);
        toast.success('Colaborador atualizado!');
      }
      setIsAdding(false);
      setEditingId(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  // ====== DESATIVAR (soft) — substitui o antigo "Eliminar" ======
  const handleAbrirDesativar = (user) => {
    setDesativarTarget({ id: user.id, nome: user.nome });
    setDesativarMotivo('saida_empresa');
    setDesativarObs('');
  };

  const handleConfirmarDesativar = async () => {
    if (!desativarTarget) return;
    try {
      const res = await api.post(
        `/admin/dados-teste/colaboradores/${desativarTarget.id}/desativar`,
        { motivo: desativarMotivo, observacoes: desativarObs }
      );
      const data = res.data;
      const detalhes = [
        `${data.pedidos_ferias_cancelados || 0} pedidos de férias cancelados`,
        `${data.ferias_definitivas_arquivadas || 0} férias futuras arquivadas`,
        `${data.ferias_dias_futuros_removidos || 0} dias futuros removidos do mapa`,
      ].join(', ');
      toast.success(`${data.colaborador_nome} desativado(a). ${detalhes}. Histórico preservado.`);
      setDesativarTarget(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desativar colaborador');
    }
  };

  // ====== REACTIVAR ======
  const handleReativar = async (user) => {
    if (!window.confirm(`Reactivar ${user.nome}?\n\nO colaborador volta a poder aceder ao sistema.\nFérias arquivadas NÃO são restauradas — terá de criar novos pedidos.`)) return;
    try {
      const res = await api.post(`/admin/dados-teste/colaboradores/${user.id}/reativar`);
      toast.success(`${res.data.colaborador_nome} reactivado(a).`);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reactivar');
    }
  };

  // ====== CLEANUP DADOS DE TESTE ======
  const handleAbrirCleanup = async () => {
    setCleanupLoading(true);
    try {
      const res = await api.get('/admin/dados-teste/preview');
      setCleanupPreview(res.data);
      setShowCleanupModal(true);
    } catch (error) {
      toast.error('Erro ao obter pré-visualização');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleConfirmarCleanup = async () => {
    setCleanupLoading(true);
    try {
      const res = await api.post('/admin/dados-teste/cleanup', { confirm: true });
      const d = res.data;
      toast.success(
        `Cleanup concluído: ${d.users_desativados} users, ${d.pedidos_ferias_arquivados} pedidos, ${d.ferias_definitivas_arquivadas} férias, ${d.avaliacoes_arquivadas} avaliações arquivadas.`
      );
      setShowCleanupModal(false);
      setCleanupPreview(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no cleanup');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
  };

  if (loading) return <div>Carregando...</div>;

  // Filtro: por defeito esconde inativos
  const visibleUsers = showInativos ? users : users.filter((u) => u.ativo !== false);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h2 className="text-2xl font-display font-bold">Gestão de Colaboradores</h2>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none px-3 py-2 border rounded-md hover:bg-slate-50">
            <input
              type="checkbox"
              checked={showInativos}
              onChange={(e) => setShowInativos(e.target.checked)}
              data-testid="toggle-mostrar-inativos"
            />
            Mostrar inativos
          </label>
          <Button
            variant="outline"
            onClick={handleAbrirCleanup}
            disabled={cleanupLoading}
            data-testid="btn-cleanup-dados-teste"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Limpar dados de teste
          </Button>
          {!isAdding && !editingId && (
            <Button onClick={handleAdd} data-testid="btn-novo-colaborador">
              <UserPlus className="h-4 w-4 mr-2" />
              Novo Colaborador
            </Button>
          )}
        </div>
      </div>

      {(isAdding || editingId) && (
        <Card className="border-primary/20 bg-gradient-card">
          <CardHeader>
            <CardTitle>{isAdding ? 'Novo Colaborador' : 'Editar Colaborador'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Username *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  disabled={!isAdding}
                  placeholder="username"
                />
              </div>
              <div className="space-y-2">
                <Label>Nome Completo *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Nome completo"
                />
              </div>
              <div className="space-y-2">
                <Label>Palavra-passe {isAdding && '*'}</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={isAdding ? 'Palavra-passe' : 'Deixe em branco para manter'}
                />
              </div>
              <div className="space-y-2">
                <Label>Permissão</Label>
                <NativeSelect
                  value={formData.role}
                  onChange={(e) => {
                    const newRole = e.target.value;
                    setFormData({
                      ...formData,
                      role: newRole,
                      mostrar_em_horario: newRole === 'admin' ? false : true,
                    });
                  }}
                >
                  <option value="colaborador">Colaborador</option>
                  <option value="responsavel">Responsável</option>
                  <option value="admin">Administrador</option>
                </NativeSelect>
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <NativeSelect
                  value={formData.ativo ? 'true' : 'false'}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.value === 'true' })}
                >
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </NativeSelect>
              </div>
              <div className="space-y-2 col-span-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="mostrar_em_horario"
                    checked={formData.mostrar_em_horario}
                    onChange={(e) =>
                      setFormData({ ...formData, mostrar_em_horario: e.target.checked })
                    }
                    className="w-4 h-4 text-primary bg-background border-gray-300 rounded focus:ring-primary focus:ring-2"
                  />
                  <Label htmlFor="mostrar_em_horario" className="cursor-pointer">
                    Mostrar em Horário
                  </Label>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button onClick={handleSave}>
                <Save className="h-4 w-4 mr-2" />
                Guardar
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {visibleUsers.map((user) => {
          const inativo = user.ativo === false;
          return (
            <Card key={user.id} className={`hover:shadow-md transition-all ${inativo ? 'opacity-70' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-display font-semibold text-lg">{user.nome}</h3>
                      {user.role === 'admin' && <Badge variant="default">Admin</Badge>}
                      {user.role === 'responsavel' && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                          Responsável
                        </Badge>
                      )}
                      {user.role === 'colaborador' && <Badge variant="secondary">Colaborador</Badge>}
                      {inativo && <Badge variant="destructive">Inativo</Badge>}
                      {user.mostrar_em_horario && !inativo && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          📅 Horário
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>
                        Username: <strong>{user.username}</strong>
                      </p>
                      {inativo && user.motivo_desativacao && (
                        <p className="text-xs italic">
                          Motivo: {user.motivo_desativacao}
                          {user.data_desativacao && ` · ${user.data_desativacao.slice(0, 10)}`}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="icon" onClick={() => handleEdit(user)} data-testid={`btn-editar-colab-${user.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    {inativo ? (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleReativar(user)}
                        className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        title="Reactivar colaborador"
                        data-testid={`btn-reativar-colab-${user.id}`}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleAbrirDesativar(user)}
                        className="text-destructive hover:bg-destructive/10"
                        title="Desativar colaborador (saída da empresa)"
                        data-testid={`btn-desativar-colab-${user.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {visibleUsers.length === 0 && (
          <p className="text-center text-muted-foreground py-8">
            {showInativos ? 'Nenhum colaborador encontrado.' : 'Nenhum colaborador ativo. Active "Mostrar inativos" para ver todos.'}
          </p>
        )}
      </div>

      {/* Modal: Desativar colaborador */}
      <Dialog open={!!desativarTarget} onOpenChange={(open) => !open && setDesativarTarget(null)}>
        <DialogContent data-testid="modal-desativar-colaborador">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Desativar colaborador
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <p>
                Tem a certeza que quer desativar <strong>{desativarTarget?.nome}</strong>?
              </p>
              <p>
                O colaborador deixa de ter acesso mas o histórico (vendas, pontos, registos
                de ponto, avaliações antigas, férias passadas, documentos, mensagens) é{' '}
                <strong>preservado integralmente</strong>.
              </p>
              <p>
                Pedidos de férias pendentes e férias futuras serão arquivados (recuperáveis).
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Motivo</Label>
              <NativeSelect
                value={desativarMotivo}
                onChange={(e) => setDesativarMotivo(e.target.value)}
              >
                <option value="saida_empresa">Saída da empresa</option>
                <option value="fim_contrato">Fim de contrato</option>
                <option value="licenca_prolongada">Licença prolongada</option>
                <option value="outro">Outro</option>
              </NativeSelect>
            </div>
            <div className="space-y-1">
              <Label>Observações (opcional)</Label>
              <Input
                value={desativarObs}
                onChange={(e) => setDesativarObs(e.target.value)}
                placeholder="Notas internas (opcional)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDesativarTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmarDesativar}
              data-testid="btn-confirmar-desativar"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Confirmar desativação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Cleanup dados de teste */}
      <Dialog open={showCleanupModal} onOpenChange={setShowCleanupModal}>
        <DialogContent className="max-w-2xl" data-testid="modal-cleanup-dados-teste">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-600" />
              Limpar dados de teste
            </DialogTitle>
            <DialogDescription>
              Os utilizadores e dados abaixo serão arquivados (recuperáveis). Nenhum dado é
              apagado fisicamente.
            </DialogDescription>
          </DialogHeader>
          {cleanupPreview && (
            <div className="space-y-3 max-h-96 overflow-y-auto text-sm">
              <div className="grid grid-cols-2 gap-2 p-3 bg-amber-50 rounded">
                <div>
                  <strong>{cleanupPreview.total_users_teste}</strong> utilizadores
                </div>
                <div>
                  <strong>{cleanupPreview.total_pedidos_ferias_a_arquivar}</strong> pedidos de férias
                </div>
                <div>
                  <strong>{cleanupPreview.total_ferias_definitivas_a_arquivar}</strong> férias definitivas
                </div>
                <div>
                  <strong>{cleanupPreview.total_avaliacoes_a_arquivar}</strong> avaliações
                </div>
              </div>
              {cleanupPreview.detalhe?.test_users?.length > 0 && (
                <div>
                  <p className="font-semibold mb-1">Utilizadores:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-xs">
                    {cleanupPreview.detalhe.test_users.map((u, i) => (
                      <li key={i}>
                        <strong>{u.nome}</strong> ({u.username}) — {u.role}
                        {u.ja_inativo && <span className="text-muted-foreground"> · já inativo</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCleanupModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmarCleanup}
              disabled={cleanupLoading || cleanupPreview?.total_users_teste === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="btn-confirmar-cleanup"
            >
              {cleanupLoading ? 'A processar...' : 'Confirmar limpeza'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ColaboradoresTab;
